// client/src/components/job/MeasurementOrdersSection.jsx
// Displays Hover / EagleView measurement orders tied to a job, with status
// badges and a Refresh button to poll the provider for the latest status.
import React, { useState, useEffect, useCallback } from 'react';
import { BLUE } from './constants';

const PROVIDER_LABELS = { hover: 'Hover', eagleview: 'EagleView' };

const STATUS_META = {
  // Hover
  created:      { label: 'Created',          color: '#6B7280', bg: '#F3F4F6' },
  started:      { label: 'Started',          color: '#D97706', bg: '#FFFBEB' },
  processing:   { label: 'Processing',       color: '#2563EB', bg: '#EFF6FF' },
  needs_review: { label: 'Needs Review',     color: '#7C3AED', bg: '#F5F3FF' },
  complete:     { label: 'Complete ✓',       color: '#065F46', bg: '#D1FAE5' },
  failed:       { label: 'Failed',           color: '#991B1B', bg: '#FEE2E2' },
  // EagleView
  placed:       { label: 'Placed',           color: '#6B7280', bg: '#F3F4F6' },
  on_hold:      { label: 'On Hold',          color: '#D97706', bg: '#FFFBEB' },
};

function statusMeta(status) {
  return STATUS_META[(status || '').toLowerCase()] || { label: status || 'Unknown', color: '#6B7280', bg: '#F3F4F6' };
}

function StatusBadge({ status }) {
  const { label, color, bg } = statusMeta(status);
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      color,
      background: bg,
      border: `1px solid ${color}33`,
    }}>
      {label}
    </span>
  );
}

export default function MeasurementOrdersSection({ jobId, token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null); // order db id

  const headers = { 'x-auth-token': token };

  const fetchOrders = useCallback(() => {
    setLoading(true);
    fetch(`/api/jobs/${jobId}/measurement-orders`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setOrders(d.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jobId, token]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleRefresh = async (order) => {
    setRefreshing(order.id);
    try {
      const res = await fetch(`/api/jobs/${jobId}/measurement-orders/${order.id}/refresh`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setOrders((prev) => prev.map((o) => (o.id === order.id ? data.order : o)));
      }
    } catch (_) {}
    setRefreshing(null);
  };

  if (loading) return null;
  if (!orders.length) return null;

  const isDone = (status) => ['complete', 'failed'].includes((status || '').toLowerCase());

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ color: BLUE, marginBottom: 12, fontSize: 15 }}>Measurement Orders</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {orders.map((order) => {
          const providerLabel = PROVIDER_LABELS[order.provider] || order.provider;
          const createdDate = order.created_at
            ? new Date(order.created_at).toLocaleDateString()
            : '—';
          const done = isDone(order.status);

          return (
            <div
              key={order.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                background: '#FAFAFA',
                flexWrap: 'wrap',
              }}
            >
              {/* Provider icon label */}
              <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: BLUE,
                minWidth: 72,
              }}>
                {providerLabel}
              </span>

              {/* Order ID */}
              <span style={{ fontSize: 12, color: '#374151', fontFamily: 'monospace', flex: 1 }}>
                #{order.order_id}
              </span>

              {/* Date */}
              <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                {createdDate}
              </span>

              {/* Status badge */}
              <StatusBadge status={order.status} />

              {/* Refresh button — only show for in-progress orders */}
              {!done && (
                <button
                  onClick={() => handleRefresh(order)}
                  disabled={refreshing === order.id}
                  style={{
                    padding: '4px 12px',
                    fontSize: 11,
                    border: `1px solid ${BLUE}`,
                    borderRadius: 6,
                    background: 'white',
                    color: BLUE,
                    cursor: refreshing === order.id ? 'not-allowed' : 'pointer',
                    opacity: refreshing === order.id ? 0.6 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {refreshing === order.id ? 'Checking…' : '↻ Refresh'}
                </button>
              )}

              {/* Result summary when complete */}
              {order.result_json && (
                <div style={{
                  width: '100%',
                  marginTop: 6,
                  padding: '8px 10px',
                  background: '#F0FDF4',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#065F46',
                }}>
                  <strong>Measurements available.</strong>{' '}
                  {order.result_json?.roofAreaFt2 && (
                    <span>Roof area: ~{Number(order.result_json.roofAreaFt2).toLocaleString()} sq ft. </span>
                  )}
                  {order.result_json?.totalWallAreaFt2 && (
                    <span>Wall area: ~{Number(order.result_json.totalWallAreaFt2).toLocaleString()} sq ft.</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
